import { lowerFirst } from 'lodash'
import { formatDateByUserTimeZone } from './formatDateByUserTimeZone'
import type { ISisSyncRun, SisSyncRunOutcome, SisSyncRunTrigger } from '../models/SisSyncRun'

// The single owner of the CR-390 display rules for the SIS sync runs list and the run detail page,
// so the same run reads the same on both. Neither surface keeps its own copy.

// MUI Chip colours that render in this theme. Never `secondary`: the theme leaves `secondary.main`
// empty, and an outlined secondary Chip throws during render, which blanks the whole app.
export type RunChipColor = 'default' | 'primary' | 'success' | 'warning' | 'error'

export interface RunStatusChip {
  label: string
  color: RunChipColor
  variant: 'outlined'
}

export const OUTCOME_LABELS: Record<SisSyncRunOutcome, string> = {
  RUNNING: 'Running',
  COMPLETED: 'Completed',
  COMPLETED_WITH_ERRORS: 'Completed with errors',
  FAILED: 'Failed',
  REVOKED: 'Revoked',
  SCHEDULED: 'Scheduled'
}

export const TRIGGER_LABELS: Record<SisSyncRunTrigger, string> = {
  SCHEDULED: 'Scheduled',
  ON_DEMAND: 'On demand'
}

// What the trigger badge's hover explains.
const TRIGGER_DESCRIPTIONS: Record<SisSyncRunTrigger, string> = {
  SCHEDULED: 'Started automatically by the SIS sync schedule.',
  ON_DEMAND: 'Started manually, outside the SIS sync schedule.'
}

export const getRunTriggerDescription = (trigger: SisSyncRunTrigger | null | undefined): string =>
  TRIGGER_DESCRIPTIONS[trigger as SisSyncRunTrigger] ?? 'How this run was started.'

// A trigger added after this was written shows its raw value.
export const getRunTriggerLabel = (trigger: SisSyncRunTrigger | null | undefined): string =>
  TRIGGER_LABELS[trigger as SisSyncRunTrigger] ?? trigger ?? ''

// Progress of a running run (rule 2): "File 3 of 12", or "Starting" before the files are counted.
export const getRunProgress = (run: Pick<ISisSyncRun, 'currentFileNumber' | 'totalFileCount'>): string =>
  run.totalFileCount > 0 ? `File ${run.currentFileNumber} of ${run.totalFileCount}` : 'Starting'

type RunStatusFields = Pick<ISisSyncRun, 'outcome' | 'failureReason' | 'currentFileNumber' | 'totalFileCount'>

// Keyed on outcome, never the raw status (rule 1). An SFTP_UPLOAD failure means the import itself
// landed, so it reads amber rather than red (rule 3); any other reason, including one added after
// this was written, is a plain failure (rule 5). An outcome added later shows its raw value in grey.
// The runs list has no other place for progress, so it passes `withProgress` and gets
// "Running: file 3 of 12"; the detail page shows progress on its meta line instead.
export const getRunStatusChip = (run: RunStatusFields, { withProgress = false } = {}): RunStatusChip => {
  const outcome: string = run.outcome
  const chip = (label: string, color: RunChipColor): RunStatusChip => ({ label, color, variant: 'outlined' })

  switch (outcome) {
    case 'RUNNING':
      return chip(withProgress ? `${OUTCOME_LABELS.RUNNING}: ${lowerFirst(getRunProgress(run))}` : OUTCOME_LABELS.RUNNING, 'primary')
    case 'COMPLETED':
      return chip(OUTCOME_LABELS.COMPLETED, 'success')
    case 'COMPLETED_WITH_ERRORS':
      return chip(OUTCOME_LABELS.COMPLETED_WITH_ERRORS, 'warning')
    case 'FAILED':
      return run.failureReason === 'SFTP_UPLOAD'
        ? chip('Imported, results not sent', 'warning')
        : chip(OUTCOME_LABELS.FAILED, 'error')
    case 'REVOKED':
      return chip(OUTCOME_LABELS.REVOKED, 'default')
    case 'SCHEDULED':
      return chip(OUTCOME_LABELS.SCHEDULED, 'default')
    default:
      return chip(String(outcome ?? ''), 'default')
  }
}

// Timestamps arrive in UTC and render in the browser's local time (rule 8). A missing or malformed
// timestamp reads as '' rather than throwing during render. The two surfaces format on purpose
// differently: the header reads as a sentence, the table column matches the Emails table.
export const formatRunTimestamp = (timestamp: string | null | undefined): string =>
  formatDateByUserTimeZone(timestamp, 'MMM d, yyyy h:mm a')

export const formatRunListTimestamp = (timestamp: string | null | undefined): string =>
  formatDateByUserTimeZone(timestamp, 'MM/dd/yyyy h:mm a')

export const formatRunDuration = (durationMs: number): string => {
  const totalSeconds = Math.round(durationMs / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) return `${hours}h ${minutes}m`
  if (minutes > 0) return `${minutes}m ${seconds}s`
  return `${seconds}s`
}
